// frontend/src/hooks/useTripTracking.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { t } from 'i18next';
import WebSocketService from '../services/WebSocketService';

import etaService from '../services/etaService';
import { usePreferences } from '../contexts/PreferenceContext';
import { isTripEntityMismatch } from '../utils/entityMatch';
import {
  createCanonicalLiveEtaPayload,
  getEtaTimestampMs,
  isNoDataEtaPayload,
  shouldAcceptEtaUpdate
} from '../utils/liveEta';
import logger, { getSafeErrorDetails } from '../utils/logger';

const ETA_DISABLED_REASON = 'eta_disabled_by_entity_preference';

const isDistanceOnlyEtaPayload = (payload = null) => (
  payload?.provider === 'none'
  || payload?.eta_disabled_reason === ETA_DISABLED_REASON
);

const shouldRejectEtaForNoneSource = (effectiveEtaSource, payload = null) => (
  effectiveEtaSource === 'none'
  && Boolean(payload)
  && !isDistanceOnlyEtaPayload(payload)
);

const stripOperationalDelayPresentation = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  const distanceOnlyPayload = { ...payload };
  delete distanceOnlyPayload.operational_delay_declarations;
  delete distanceOnlyPayload.operational_delay_projection_waypoint_order_version;
  return distanceOnlyPayload;
};

// Normalize location payloads so downstream map logic receives both lat/lng and latitude/longitude.
// Shuttle simulator publishes latitude/longitude via REST while drivers send lat/lng via WebSocket.
const normalizeLocationPayload = (payload) => {
  if (!payload) return null;

  const lat = payload.lat ?? payload.latitude ?? null;
  const lng = payload.lng ?? payload.longitude ?? null;

  if (lat === null || lng === null) {
    return { ...payload, lat, lng };
  }

  return {
    ...payload,
    lat,
    lng,
    latitude: payload.latitude ?? lat,
    longitude: payload.longitude ?? lng,
  };
};

const calculateDistanceMeters = (lat1, lng1, lat2, lng2) => {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const LOCATION_POSTING_POLICY = {
  NORMAL_MOVEMENT_THRESHOLD_METERS: 20,
  ACTIVITY_STOP_MOVEMENT_THRESHOLD_METERS: 10,
  NEAR_ACTIVITY_STOP_RADIUS_METERS: 100,
  TRANSITION_POSITION_MAX_AGE_MS: 2000,
  MIN_POST_INTERVAL_MS: 5000,
};
export const GPS_FEED_FRESHNESS_MS = 90000;
const SELECTED_GPS_FEED_STATUSES = new Set(['scheduled', 'dispatched', 'in_progress']);

const TRANSITION_SENSITIVE_WAYPOINT_STATES = new Set(['dwelling', 'departing']);

const toFiniteNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const createInactiveSelectedGpsFeedState = (overrides = {}) => ({
  selectedGpsFeedActive: false,
  selectedGpsFeedSource: null,
  selectedGpsFeedDeviceId: null,
  selectedGpsFeedLastReceivedAt: null,
  selectedGpsFeedStale: false,
  selectedGpsFeedEntityId: null,
  selectedGpsFeedTripId: null,
  ...overrides
});

const normalizeSelectedGpsSource = (selectedGpsSource) => {
  if (selectedGpsSource?.type === 'phone') {
    return { type: 'phone', deviceId: null, key: 'phone' };
  }

  if (selectedGpsSource?.type === 'device') {
    const deviceId = toFiniteNumber(selectedGpsSource.deviceId);
    if (deviceId !== null) {
      return { type: 'device', deviceId, key: `device:${deviceId}` };
    }
  }

  return { type: null, deviceId: null, key: 'unresolved' };
};

const getLiveSelectedGpsFeedUpdate = ({
  payload,
  selectedGpsSource,
  status,
  nowMs
}) => {
  if (!payload || !SELECTED_GPS_FEED_STATUSES.has(status) || !selectedGpsSource?.type) {
    return null;
  }

  if (payload.event !== 'live_location') {
    return null;
  }

  const serverReceivedAt = payload.server_received_at;
  const serverReceivedAtMs = Date.parse(serverReceivedAt);
  if (!serverReceivedAt || Number.isNaN(serverReceivedAtMs)) {
    return null;
  }

  if (nowMs - serverReceivedAtMs > GPS_FEED_FRESHNESS_MS) {
    return null;
  }

  if (
    selectedGpsSource.type === 'phone'
    && payload.source === 'phone'
    && payload.source_device_id === null
  ) {
    return {
      selectedGpsFeedActive: true,
      selectedGpsFeedSource: 'phone',
      selectedGpsFeedDeviceId: null,
      selectedGpsFeedLastReceivedAt: serverReceivedAt,
      selectedGpsFeedStale: false,
      serverReceivedAtMs
    };
  }

  const payloadDeviceId = toFiniteNumber(payload.source_device_id);
  if (
    selectedGpsSource.type === 'device'
    && payload.source === 'device'
    && payloadDeviceId !== null
    && payloadDeviceId === selectedGpsSource.deviceId
  ) {
    return {
      selectedGpsFeedActive: true,
      selectedGpsFeedSource: 'device',
      selectedGpsFeedDeviceId: selectedGpsSource.deviceId,
      selectedGpsFeedLastReceivedAt: serverReceivedAt,
      selectedGpsFeedStale: false,
      serverReceivedAtMs
    };
  }

  return null;
};

const normalizeWaypointState = (state) => (
  typeof state === 'string' ? state.toLowerCase() : null
);

const getWaypointId = (waypoint) => (
  waypoint?.waypoint_id ?? waypoint?.waypointId ?? waypoint?.id ?? null
);

const getWaypointLatitude = (waypoint) => (
  toFiniteNumber(waypoint?.latitude ?? waypoint?.lat)
);

const getWaypointLongitude = (waypoint) => (
  toFiniteNumber(waypoint?.longitude ?? waypoint?.lng)
);

const getWaypointStatePayload = (waypoint, waypointStates) => {
  const waypointId = getWaypointId(waypoint);
  if (waypointId === null || waypointId === undefined || !waypointStates) {
    return null;
  }

  if (typeof waypointStates.get === 'function') {
    return waypointStates.get(waypointId)
      ?? waypointStates.get(String(waypointId))
      ?? waypointStates.get(Number(waypointId))
      ?? null;
  }

  return waypointStates[waypointId] ?? waypointStates[String(waypointId)] ?? null;
};

const getWaypointLifecycleState = (waypoint, waypointStates) => {
  const statePayload = getWaypointStatePayload(waypoint, waypointStates);
  const explicitState = normalizeWaypointState(
    statePayload?.state ?? waypoint?.state ?? waypoint?.waypoint_state ?? waypoint?.lifecycle_state
  );

  if (explicitState) {
    return explicitState;
  }

  if (
    waypoint?.is_passed === true
    || waypoint?.is_passed === 1
    || waypoint?.waypoint_departure_time
    || waypoint?.departure_time
  ) {
    return 'passed';
  }

  if (
    waypoint?.waypoint_arrival_time
    || waypoint?.arrival_time
    || waypoint?.arrived_at
  ) {
    return 'dwelling';
  }

  return null;
};

const waypointHasActivity = (waypoint) => {
  if (waypoint?.point_type !== 'stop') {
    return false;
  }

  return (toFiniteNumber(waypoint?.pickup_count) || 0) > 0
    || (toFiniteNumber(waypoint?.dropoff_count) || 0) > 0;
};

const isWaypointPassed = (waypoint, state) => (
  state === 'passed'
  || waypoint?.is_passed === true
  || waypoint?.is_passed === 1
  || Boolean(waypoint?.waypoint_departure_time || waypoint?.departure_time)
);

const isWaypointSkipped = (waypoint) => (
  waypoint?.is_skipped === true || waypoint?.is_skipped === 1
);

const getTripStopCandidates = (tripData) => {
  if (Array.isArray(tripData?.effective_stops) && tripData.effective_stops.length > 0) {
    return tripData.effective_stops;
  }

  if (Array.isArray(tripData?.stops) && tripData.stops.length > 0) {
    return tripData.stops;
  }

  return Array.isArray(tripData?.waypoints) ? tripData.waypoints : [];
};

const mergePolicyWaypoint = (tripStop, waypoint) => {
  const merged = { ...tripStop, ...waypoint };

  if (!tripStop) {
    return merged;
  }

  // Parent trip data is refreshed by passage/status flows and can be newer
  // than a cached ETA payload. Preserve truthy completion state from that
  // source so stale ETA rows cannot re-enable near-stop posting for a passed
  // activity stop.
  if (tripStop.is_passed === true || tripStop.is_passed === 1) {
    merged.is_passed = tripStop.is_passed;
  }
  if (tripStop.is_skipped === true || tripStop.is_skipped === 1) {
    merged.is_skipped = tripStop.is_skipped;
  }
  if (tripStop.waypoint_departure_time) {
    merged.waypoint_departure_time = tripStop.waypoint_departure_time;
  }
  if (tripStop.departure_time) {
    merged.departure_time = tripStop.departure_time;
  }
  if (tripStop.waypoint_arrival_time) {
    merged.waypoint_arrival_time = tripStop.waypoint_arrival_time;
  }
  if (tripStop.arrival_time) {
    merged.arrival_time = tripStop.arrival_time;
  }

  return merged;
};

const buildPostingPolicyWaypoints = ({ etaPayload, tripData }) => {
  const tripStops = getTripStopCandidates(tripData);
  const tripStopById = new Map(
    tripStops
      .map((stop) => [getWaypointId(stop), stop])
      .filter(([waypointId]) => waypointId !== null && waypointId !== undefined)
  );
  const seenWaypointIds = new Set();
  const waypoints = [];

  const addWaypoint = (waypoint, preferEtaOrder = false) => {
    const waypointId = getWaypointId(waypoint);
    if (waypointId === null || waypointId === undefined || seenWaypointIds.has(String(waypointId))) {
      return;
    }

    const tripStop = tripStopById.get(waypointId) ?? tripStopById.get(String(waypointId)) ?? tripStopById.get(Number(waypointId));
    waypoints.push(preferEtaOrder ? mergePolicyWaypoint(tripStop, waypoint) : waypoint);
    seenWaypointIds.add(String(waypointId));
  };

  if (Array.isArray(etaPayload?.waypoints)) {
    etaPayload.waypoints.forEach((waypoint) => addWaypoint(waypoint, true));
  }

  tripStops.forEach((waypoint) => addWaypoint(waypoint, false));

  return waypoints;
};

const buildLocationPostingPolicy = ({
  movementThresholdMeters,
  reason,
  targetWaypoint = null,
  distanceToTargetMeters = null,
}) => ({
  minPostIntervalMs: LOCATION_POSTING_POLICY.MIN_POST_INTERVAL_MS,
  movementThresholdMeters,
  transitionSensitive: movementThresholdMeters === LOCATION_POSTING_POLICY.ACTIVITY_STOP_MOVEMENT_THRESHOLD_METERS,
  stalePositionMaxAgeMs: movementThresholdMeters === LOCATION_POSTING_POLICY.ACTIVITY_STOP_MOVEMENT_THRESHOLD_METERS
    ? LOCATION_POSTING_POLICY.TRANSITION_POSITION_MAX_AGE_MS
    : null,
  reason,
  targetWaypointId: targetWaypoint ? getWaypointId(targetWaypoint) : null,
  distanceToTargetMeters,
});

export const getLocationPostingPolicy = ({
  location = null,
  etaPayload = null,
  tripData = null,
  waypointStates = null,
} = {}) => {
  const normalPolicy = buildLocationPostingPolicy({
    movementThresholdMeters: LOCATION_POSTING_POLICY.NORMAL_MOVEMENT_THRESHOLD_METERS,
    reason: 'normal',
  });
  const waypoints = buildPostingPolicyWaypoints({ etaPayload, tripData });

  // A served activity stop in the internal transition states needs finer
  // movement samples until the backend marks it passed. This is keyed to the
  // active state payload, not arbitrary proximity to another stop on the loop.
  const transitionWaypoint = waypoints.find((waypoint) => {
    const lifecycleState = getWaypointLifecycleState(waypoint, waypointStates);
    return waypointHasActivity(waypoint)
      && TRANSITION_SENSITIVE_WAYPOINT_STATES.has(lifecycleState)
      && !isWaypointPassed(waypoint, lifecycleState)
      && !isWaypointSkipped(waypoint);
  });

  if (transitionWaypoint) {
    return buildLocationPostingPolicy({
      movementThresholdMeters: LOCATION_POSTING_POLICY.ACTIVITY_STOP_MOVEMENT_THRESHOLD_METERS,
      reason: `activity_stop_${getWaypointLifecycleState(transitionWaypoint, waypointStates)}`,
      targetWaypoint: transitionWaypoint,
      distanceToTargetMeters: getDistanceToWaypoint(location, transitionWaypoint),
    });
  }

  const activeExpectedWaypoint = waypoints.find((waypoint) => {
    const lifecycleState = getWaypointLifecycleState(waypoint, waypointStates);
    return !isWaypointPassed(waypoint, lifecycleState) && !isWaypointSkipped(waypoint);
  });

  if (!activeExpectedWaypoint || !waypointHasActivity(activeExpectedWaypoint)) {
    return normalPolicy;
  }

  const distanceToTargetMeters = getDistanceToWaypoint(location, activeExpectedWaypoint);
  if (
    distanceToTargetMeters !== null
    && distanceToTargetMeters <= LOCATION_POSTING_POLICY.NEAR_ACTIVITY_STOP_RADIUS_METERS
  ) {
    return buildLocationPostingPolicy({
      movementThresholdMeters: LOCATION_POSTING_POLICY.ACTIVITY_STOP_MOVEMENT_THRESHOLD_METERS,
      reason: 'near_active_activity_stop',
      targetWaypoint: activeExpectedWaypoint,
      distanceToTargetMeters,
    });
  }

  return normalPolicy;
};

const getDistanceToWaypoint = (location, waypoint) => {
  const locationLat = toFiniteNumber(location?.lat ?? location?.latitude);
  const locationLng = toFiniteNumber(location?.lng ?? location?.longitude);
  const waypointLat = getWaypointLatitude(waypoint);
  const waypointLng = getWaypointLongitude(waypoint);

  if (
    locationLat === null
    || locationLng === null
    || waypointLat === null
    || waypointLng === null
  ) {
    return null;
  }

  return calculateDistanceMeters(locationLat, locationLng, waypointLat, waypointLng);
};

export const shouldPostLocationUpdate = ({
  location,
  lastPostedLocation,
  lastLogTime,
  now,
  postingPolicy,
  maxTimeWithoutLogMs,
  stationarySpeedThreshold,
}) => {
  const movementAnchor = lastPostedLocation || null;
  const movementMeters = movementAnchor
    ? calculateDistanceMeters(movementAnchor.lat, movementAnchor.lng, location.lat, location.lng)
    : null;
  const hasMovedSignificantly = !movementAnchor
    || movementMeters >= postingPolicy.movementThresholdMeters;
  const locationSpeed = toFiniteNumber(location.speed) || 0;
  const isStationary = locationSpeed < stationarySpeedThreshold;
  const timeSinceLastLog = lastLogTime ? now - lastLogTime : maxTimeWithoutLogMs;
  const maxTimeElapsed = timeSinceLastLog >= maxTimeWithoutLogMs;

  return {
    shouldPost: hasMovedSignificantly || (maxTimeElapsed && isStationary),
    movementMeters,
    hasMovedSignificantly,
    isStationary,
    maxTimeElapsed,
  };
};

const getEtaLogSummary = (etaPayload) => ({
  cached: Boolean(etaPayload?.cached),
  provider: etaPayload?.provider || null,
  waypointCount: Array.isArray(etaPayload?.waypoints) ? etaPayload.waypoints.length : 0,
  hasTimestamp: Boolean(etaPayload?.timestamp),
  activeEtaState: etaPayload?.active_eta_state || null,
});

const getSafeErrorContext = (error, context = {}) => ({
  ...(getSafeErrorDetails(error) || {}),
  ...context,
});

export { getEtaTimestampMs, shouldAcceptEtaUpdate };

export const useTripTracking = (
  tripId,
  status,
  isDriver,
  onLocationUpdate,
  onEtaUpdate,
  onStatusUpdate,
  onWaypointStateChange,
  onArchivedTypeChange,
  currentEntity,
  tripData,
  callbacks = {}
) => {
  const {
    onWaypointOrderChanged = null,  // Callback when waypoint order changes (reorderable trips)
    onWaypointDeviationAlert = null, // Callback when driver deviates from expected order
    onGpsTrailHistory = null, // Optional callback when GPS trail history is received
    onTripBookingsChanged = null, // Callback when bookings change (refreshes is_skipped/cumulative)
    onTripPlanUpdated = null, // Callback when plan polylines/segments update
    onGpsSourceConflict = null, // Minimal GPS device ownership conflict signal
    selectedGpsSource = null, // Display-only selected feed source, separate from phone watcher activation
  } = callbacks || {};

  const [gpsActive, setGpsActive] = useState(false);
  const [gpsError, setGpsError] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [, setWakeLock] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [etaData, setEtaData] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);
  const [etaError, setEtaError] = useState(null);
  const [gpsTrail, setGpsTrail] = useState([]);
  const [selectedGpsFeedState, setSelectedGpsFeedState] = useState(createInactiveSelectedGpsFeedState);
  const { entitySettings } = usePreferences();
  const effectiveEtaSource = entitySettings?.eta_source || currentEntity?.eta_source || null;
  const connectionRef = useRef(null);
  const lastLocationRef = useRef(null);
  const lastPostedLocationRef = useRef(null);
  const lastLogTimeRef = useRef(null);
  const latestAcceptedEtaRef = useRef(null);
  const referenceRequestGeneration = useRef(0);
  const latestWaypointStatesRef = useRef(new Map());
  const latestTripDataRef = useRef(tripData);
  const selectedGpsSourceType = selectedGpsSource?.type;
  const selectedGpsSourceDeviceId = selectedGpsSource?.deviceId;
  const selectedGpsSourceForFeed = useMemo(
    () => normalizeSelectedGpsSource({
      type: selectedGpsSourceType,
      deviceId: selectedGpsSourceDeviceId
    }),
    [selectedGpsSourceType, selectedGpsSourceDeviceId]
  );
  const selectedGpsSourceForFeedRef = useRef(selectedGpsSourceForFeed);
  const feedStatusTripStatusRef = useRef(status);
  const selectedGpsFeedExpiryTimerRef = useRef(null);
  const isEntityMismatch = isTripEntityMismatch(tripData, currentEntity);
  const hasConnectionTripData = Boolean(tripData);
  const connectionTripStatus = tripData?.status;
  const connectionTripDepartureTime = tripData?.departureTime;
  const connectionTripDepartureTimeSnake = tripData?.departure_time;
  const connectionTripDate = tripData?.tripDate;
  const connectionTripDateSnake = tripData?.trip_date;
  const connectionTripData = useMemo(() => {
    if (!hasConnectionTripData) return null;

    // WebSocketService only needs activation fields at connect time. Keeping
    // this payload scalar-driven preserves the no-reconnect invariant when a
    // parent rerenders with an equivalent tripData object.
    return {
      status: connectionTripStatus,
      departureTime: connectionTripDepartureTime,
      departure_time: connectionTripDepartureTimeSnake,
      tripDate: connectionTripDate,
      trip_date: connectionTripDateSnake,
    };
  }, [
    hasConnectionTripData,
    connectionTripStatus,
    connectionTripDepartureTime,
    connectionTripDepartureTimeSnake,
    connectionTripDate,
    connectionTripDateSnake,
  ]);
  const GPS_TRAIL_MIN_DISTANCE_METERS = 10;
  const GPS_TRAIL_MAX_POINTS = 1000;

  useEffect(() => {
    setGpsTrail([]);
    latestAcceptedEtaRef.current = null;
    latestWaypointStatesRef.current = new Map();
    lastPostedLocationRef.current = null;
    ++referenceRequestGeneration.current;
    return () => { ++referenceRequestGeneration.current; };
  }, [currentEntity?.entityId, tripId]);

  useEffect(() => {
    latestTripDataRef.current = tripData;
  }, [tripData]);

  const clearSelectedGpsFeedExpiryTimer = useCallback(() => {
    if (selectedGpsFeedExpiryTimerRef.current) {
      clearTimeout(selectedGpsFeedExpiryTimerRef.current);
      selectedGpsFeedExpiryTimerRef.current = null;
    }
  }, []);

  const scheduleSelectedGpsFeedExpiry = useCallback((lastReceivedAt, serverReceivedAtMs) => {
    clearSelectedGpsFeedExpiryTimer();
    const remainingFreshMs = Math.max(0, GPS_FEED_FRESHNESS_MS - (Date.now() - serverReceivedAtMs));

    selectedGpsFeedExpiryTimerRef.current = setTimeout(() => {
      setSelectedGpsFeedState((previousState) => {
        if (previousState.selectedGpsFeedLastReceivedAt !== lastReceivedAt) {
          return previousState;
        }
        return {
          ...previousState,
          selectedGpsFeedActive: false,
          selectedGpsFeedStale: true
        };
      });
    }, remainingFreshMs);
  }, [clearSelectedGpsFeedExpiryTimer]);

  const resetSelectedGpsFeedState = useCallback(() => {
    clearSelectedGpsFeedExpiryTimer();
    setSelectedGpsFeedState(() => createInactiveSelectedGpsFeedState());
  }, [clearSelectedGpsFeedExpiryTimer]);

  useEffect(() => {
    // Selected feed liveness is scoped to the same entity boundary as the trip
    // WebSocket connection. Clear it when that authority changes so a previous
    // tenant's fresh feed cannot keep the badge active during context switches.
    feedStatusTripStatusRef.current = status;
    selectedGpsSourceForFeedRef.current = selectedGpsSourceForFeed;
    resetSelectedGpsFeedState();
  }, [
    currentEntity?.entityId,
    isEntityMismatch,
    resetSelectedGpsFeedState,
    selectedGpsSourceForFeed,
    status,
    tripId
  ]);

  useEffect(() => () => {
    clearSelectedGpsFeedExpiryTimer();
  }, [clearSelectedGpsFeedExpiryTimer]);

  const recordSelectedGpsFeedUpdate = useCallback((normalizedLocation) => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) {
      return false;
    }

    // Feed liveness answers whether the selected backend-authorized source is
    // currently publishing accepted live updates. It intentionally uses
    // server_received_at and runs independently from the map's GPS-sample
    // timestamp guard, which protects visual position from stale coordinates.
    const feedUpdate = getLiveSelectedGpsFeedUpdate({
      payload: normalizedLocation,
      selectedGpsSource: selectedGpsSourceForFeedRef.current,
      status: feedStatusTripStatusRef.current,
      nowMs: Date.now()
    });

    if (!feedUpdate) {
      return false;
    }

    const { serverReceivedAtMs, ...nextFeedStateWithoutScope } = feedUpdate;
    const nextFeedState = {
      ...nextFeedStateWithoutScope,
      selectedGpsFeedEntityId: currentEntity.entityId,
      selectedGpsFeedTripId: tripId
    };
    setSelectedGpsFeedState(nextFeedState);
    scheduleSelectedGpsFeedExpiry(nextFeedState.selectedGpsFeedLastReceivedAt, serverReceivedAtMs);
    return true;
  }, [currentEntity?.entityId, isEntityMismatch, scheduleSelectedGpsFeedExpiry, tripId]);

  const selectedGpsFeedReturnState = useMemo(() => {
    if (!currentEntity?.entityId || isEntityMismatch) {
      return createInactiveSelectedGpsFeedState();
    }

    if (!selectedGpsFeedState.selectedGpsFeedLastReceivedAt) {
      return selectedGpsFeedState;
    }

    const feedMatchesCurrentScope = (
      selectedGpsFeedState.selectedGpsFeedEntityId === currentEntity.entityId
      && selectedGpsFeedState.selectedGpsFeedTripId === tripId
    );

    if (!feedMatchesCurrentScope) {
      return createInactiveSelectedGpsFeedState();
    }

    const feedMatchesCurrentSelection = (
      selectedGpsSourceForFeed.type === 'phone'
      && selectedGpsFeedState.selectedGpsFeedSource === 'phone'
      && selectedGpsFeedState.selectedGpsFeedDeviceId === null
    ) || (
      selectedGpsSourceForFeed.type === 'device'
      && selectedGpsFeedState.selectedGpsFeedSource === 'device'
      && selectedGpsFeedState.selectedGpsFeedDeviceId === selectedGpsSourceForFeed.deviceId
    );

    return feedMatchesCurrentSelection
      ? selectedGpsFeedState
      : createInactiveSelectedGpsFeedState();
  }, [currentEntity?.entityId, isEntityMismatch, selectedGpsFeedState, selectedGpsSourceForFeed, tripId]);

  // Solution 1: Client-side GPS throttle + 429 handling
  // See: backend/docs/2026-01-12-GPS-TRACKING-RELIABILITY-IMPROVEMENTS.md
  const lastPostTimeRef = useRef(0);        // Timestamp of last successful POST to server
  const backoffMsRef = useRef(0);           // Additional backoff from 429 responses
  const MIN_POST_INTERVAL_MS = LOCATION_POSTING_POLICY.MIN_POST_INTERVAL_MS; // 5 seconds - matches backend rate limit

  // Solution 2: GPS stall watchdog
  const lastGpsCallbackTimeRef = useRef(Date.now());  // Track last GPS callback
  const GPS_STALL_THRESHOLD_MS = 60000;     // 60 seconds - warn driver if no GPS updates
  const GPS_STALL_CHECK_INTERVAL_MS = 30000; // Check every 30 seconds
  const [gpsStalled, setGpsStalled] = useState(false);

  // GPS tracking thresholds - tuned to reduce server load while maintaining accuracy
  // See: backend/docs/2025-12-30-LOCATION_ENDPOINT_REFACTOR_PLAN.md
  const MAX_TIME_WITHOUT_LOG_MS = 60000;    // 60 seconds (increased from 30s) - force log even if no movement
  const STATIONARY_SPEED_THRESHOLD = 3;     // 3 km/h (reduced from 5) - below this is considered stopped

  const appendGpsTrailPoint = useCallback((rawPoint) => {
    const normalized = normalizeLocationPayload(rawPoint);
    if (!normalized || normalized.lat == null || normalized.lng == null) return;

    const point = {
      ...normalized,
      lat: Number(normalized.lat),
      lng: Number(normalized.lng),
      timestamp: normalized.timestamp || new Date().toISOString()
    };

    setGpsTrail((prev) => {
      if (!prev || prev.length === 0) {
        return [point];
      }
      const last = prev[prev.length - 1];
      const sameTimestamp = last?.timestamp && point.timestamp && last.timestamp === point.timestamp;
      if (sameTimestamp) {
        return prev;
      }
      if (last?.lat != null && last?.lng != null) {
        const distance = calculateDistanceMeters(last.lat, last.lng, point.lat, point.lng);
        if (distance < GPS_TRAIL_MIN_DISTANCE_METERS) {
          return prev;
        }
      }
      const next = [...prev, point];
      if (next.length > GPS_TRAIL_MAX_POINTS) {
        return next.slice(next.length - GPS_TRAIL_MAX_POINTS);
      }
      return next;
    });
  }, [GPS_TRAIL_MIN_DISTANCE_METERS, GPS_TRAIL_MAX_POINTS]);

  // getWebSocketEta must be declared before acceptEtaUpdate so it can be listed in
  // acceptEtaUpdate's useCallback dep array. See comment on acceptEtaUpdate below.
  const getWebSocketEta = useCallback(() => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) return null;
    const clientKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
    const cachedEta = WebSocketService.getLatestEta(clientKey);

    if (shouldRejectEtaForNoneSource(effectiveEtaSource, cachedEta)) {
      // Entity-level ETA disablement is authoritative over client memory. A
      // browser can hold provider ETA from before the preference changed, so
      // clear the scoped cache instead of allowing stale minutes to render.
      WebSocketService.clearEtaData(clientKey);
      logger.debug('Cleared cached provider ETA because entity ETA source is none', {
        tripId,
        entityId: currentEntity.entityId,
        provider: cachedEta?.provider || null
      });
      return null;
    }

    if (isNoDataEtaPayload(cachedEta)) return null;
    return effectiveEtaSource === 'none' && isDistanceOnlyEtaPayload(cachedEta)
      ? stripOperationalDelayPresentation(cachedEta)
      : cachedEta;
  }, [currentEntity?.entityId, effectiveEtaSource, tripId, isEntityMismatch]);

  // IMPORTANT: acceptEtaUpdate MUST be wrapped in useCallback.
  // A new function reference on every render propagates through handleWebSocketMessage
  // (which depends on acceptEtaUpdate) into the WebSocket useEffect, causing it to
  // tear down and re-establish the connection on every render cycle. When React's
  // render gap exceeds the 300 ms graceful-disconnect window in WebSocketService,
  // this creates a new server-side WebSocket instead of reusing the existing one,
  // resulting in duplicate connections and missed heartbeats.
  // See: backend/docs/2026-03-25-TRIP-247-WEBSOCKET-DISCONNECT-PLAN.md
  const acceptEtaUpdate = useCallback((incomingEta, source = 'unknown') => {
    if (!incomingEta) {
      return false;
    }

    const clientKey = currentEntity?.entityId && tripId
      ? `entity_${currentEntity.entityId}_trip_${tripId}`
      : null;

    if (shouldRejectEtaForNoneSource(effectiveEtaSource, incomingEta)) {
      if (clientKey) {
        // WebSocketService stores received eta_update payloads before
        // broadcasting to hook subscribers. Clearing here removes that
        // already-stored provider payload from late subscribers too.
        WebSocketService.clearEtaData(clientKey);
      }
      logger.debug('Rejected provider ETA because entity ETA source is none', {
        tripId,
        source,
        provider: incomingEta?.provider || null
      });
      return false;
    }

    // Distance-only mode may still receive projection metadata because the
    // backend payload is shared with ETA-enabled consumers. Strip it before
    // the canonical cache/state boundary so no later render can revive labels.
    const presentationEta = effectiveEtaSource === 'none'
      && isDistanceOnlyEtaPayload(incomingEta)
      ? stripOperationalDelayPresentation(incomingEta)
      : incomingEta;
    const storedEta = clientKey ? WebSocketService.getLatestEta(clientKey) : null;
    const currentEta = latestAcceptedEtaRef.current ?? storedEta;
    const currentLiveEta = storedEta && !storedEta.cached && !isNoDataEtaPayload(storedEta)
      ? storedEta
      : null;
    // WebSocket reconnects, cached REST fallbacks, and in-memory socket replay
    // can arrive out of order. Accept through one timestamp-aware gate before
    // updating either React state or WebSocketService's shared ETA cache.
    const shouldAccept = shouldAcceptEtaUpdate({
      incomingEta: presentationEta,
      currentEta,
      currentLiveEta
    });

    if (!shouldAccept) {
      logger.debug('Skipping stale ETA payload', {
        tripId,
        source,
        incomingCached: Boolean(presentationEta.cached),
        incomingTimestamp: presentationEta.timestamp || null,
        currentTimestamp: currentEta?.timestamp || null,
        liveTimestamp: currentLiveEta?.timestamp || null
      });
      return false;
    }

    const canonicalEta = clientKey
      ? WebSocketService.storeLatestEta(clientKey, presentationEta)
      : createCanonicalLiveEtaPayload(presentationEta, currentEta);
    if (!canonicalEta) return false;

    latestAcceptedEtaRef.current = canonicalEta;
    if (canonicalEta.eta_retirement || isNoDataEtaPayload(canonicalEta)) {
      ++referenceRequestGeneration.current;
      setEtaData(null);
      setEtaLoading(false);
      setEtaError(null);
    }
    onEtaUpdate?.(canonicalEta);
    return true;
  }, [currentEntity?.entityId, effectiveEtaSource, onEtaUpdate, tripId]);

  const handleWebSocketMessage = useCallback((message) => {
    logger.debug('WebSocket message received', {
      tripId, 
      messageType: message.type, 
      entityId: currentEntity?.entityId,
      hasPayload: Boolean(message.payload || message.plan)
    });
    if (message.type === 'eta_local_retirement') {
      ++referenceRequestGeneration.current;
      latestAcceptedEtaRef.current = null;
      setEtaData(null);
      setEtaLoading(false);
      setEtaError(null);
      onEtaUpdate?.(null);
    } else if (message.type === 'connected') {
      setConnectionStatus('connected');
    } else if (message.type === 'connection_status') {
      setConnectionStatus(message.payload.status);
      if (message.payload.error) {
        logger.error('WebSocket connection error', {
          tripId, 
          entityId: currentEntity?.entityId, 
          error: message.payload.error 
        });
      }
    } else if (message.type === 'gps_source_conflict') {
      // Conflict messages are coordinate-free ownership diagnostics. Keep them
      // out of location normalization so they cannot activate selected-feed
      // liveness, move a pre-start marker, append trail points, or feed ETA.
      onGpsSourceConflict?.(message.payload || {});
    } else if (message.type === 'location_update') {
      const normalizedLocation = normalizeLocationPayload(message.payload);
      if (normalizedLocation) {
        const acceptedSelectedFeedUpdate = recordSelectedGpsFeedUpdate(normalizedLocation);
        if (status !== 'in_progress') {
          // Scheduled/dispatched live payloads prove selected-source liveness
          // and may move the current marker only after the selected-feed
          // validator accepts source/event/server-time provenance. Rejected
          // payloads can be stale or from a source that was just disabled or
          // switched, so they must not move the pre-start marker.
          if (!acceptedSelectedFeedUpdate) {
            return;
          }
          // Accepted pre-start liveness is still not active trip movement. Keep
          // it out of trail history, ETA inputs, distance totals, and movement
          // callbacks until the backend status is active.
          lastLocationRef.current = normalizedLocation;
          setCurrentLocation(normalizedLocation);
          return;
        }
        // BUG FIX (2026-01-30): Compare timestamps to prevent stale WebSocket data
        // from overwriting fresh local GPS position. This fixes the "driver map jumps
        // backward" issue where echoed WebSocket location overwrites recent local GPS.
        // See: docs/2026-01-30-ETA_DISTANCE_AND_REALTIME_TRACKING_BUGS.md (Bug #4)
        const wsTimestamp = normalizedLocation.timestamp
          ? new Date(normalizedLocation.timestamp).getTime()
          : 0;
        const currentTimestamp = lastLocationRef.current?.timestamp
          ? new Date(lastLocationRef.current.timestamp).getTime()
          : 0;
        const wsTimestampMs = Number.isFinite(wsTimestamp) ? wsTimestamp : 0;
        const currentTimestampMs = Number.isFinite(currentTimestamp) ? currentTimestamp : 0;

        if (wsTimestampMs > currentTimestampMs || currentTimestampMs === 0) {
          lastLocationRef.current = normalizedLocation;
          setCurrentLocation(normalizedLocation);
          appendGpsTrailPoint(normalizedLocation);
          onLocationUpdate?.(normalizedLocation);
        } else {
          logger.debug('Skipping stale WebSocket location update', {
            wsTimestampMs,
            currentTimestampMs,
            stalenessMs: currentTimestampMs - wsTimestampMs
          });
        }
      }
    } else if (message.type === 'gps_trail_history') {
      const payload = Array.isArray(message.payload) ? message.payload : [];
      const normalizedTrail = payload
        .map((item) => {
          if (!item) return null;
          if (typeof item === 'string') {
            try {
              return JSON.parse(item);
            } catch {
              return null;
            }
          }
          return item;
        })
        .map(normalizeLocationPayload)
        .filter((point) => point && point.lat != null && point.lng != null)
        .map((point) => ({
          ...point,
          lat: Number(point.lat),
          lng: Number(point.lng),
          timestamp: point.timestamp || new Date().toISOString()
        }))
        .sort((a, b) => {
          const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return aTime - bTime;
        });

      setGpsTrail(normalizedTrail);
      onGpsTrailHistory?.(normalizedTrail);
    } else if (message.type === 'eta_update') {
      acceptEtaUpdate(message.payload, 'websocket');
    } else if (message.type === 'status_update') {      
      // IMPORTANT: Pass isFromWebSocket=true to prevent infinite loop
      onStatusUpdate?.(message.payload.status, true, message.payload.archived_type);      
    } else if (message.type === 'waypoint_state_change') {
      const waypointId = message.payload?.waypointId ?? message.payload?.waypoint_id ?? null;
      if (waypointId !== null && waypointId !== undefined) {
        latestWaypointStatesRef.current.set(waypointId, message.payload);
        latestWaypointStatesRef.current.set(String(waypointId), message.payload);
      }
      onWaypointStateChange?.(message.payload);
    } else if (message.type === 'archived_type_selected') {
      ++referenceRequestGeneration.current;
      onArchivedTypeChange?.(message.payload.archived_type);
    } else if (message.type === 'waypoint_order_changed') {
      // Reorderable waypoints: order was changed by admin
      logger.debug('WebSocket waypoint_order_changed received', {
        tripId,
        entityId: currentEntity?.entityId,
        newVersion: message.payload?.newVersion ?? message.payload?.version ?? null,
        changeType: message.payload?.changeType || message.payload?.type || null,
        syncStatus: message.payload?.sync?.status || null
      });
      onWaypointOrderChanged?.(message.payload);
    } else if (message.type === 'waypoint_deviation_alert') {
      // Driver visited waypoint out of expected order
      logger.debug('WebSocket waypoint_deviation_alert received', {
        tripId,
        entityId: currentEntity?.entityId,
        expectedWaypointId: message.payload?.expectedWaypointId ?? null,
        arrivedWaypointId: message.payload?.arrivedWaypointId ?? message.payload?.arrivedAt ?? null
      });
      onWaypointDeviationAlert?.(message.payload);
    } else if (
      message.type === 'trip_bookings_changed'
      || message.type === 'operational_unavailability_changed'
      || message.type === 'trip_delay_updated'
    ) {
      // Booking created/canceled/updated — refresh trip data to update is_skipped/cumulative
      logger.debug('WebSocket trip data refresh requested', {
        tripId,
        entityId: currentEntity?.entityId,
        eventType: message.payload?.eventType || message.payload?.type || null,
        hasPayload: Boolean(message.payload)
      });
      const refreshResult = onTripBookingsChanged?.({
        ...(message.payload || {}),
        eventType: message.type
      });
      if (message.type === 'trip_delay_updated' && refreshResult?.catch) {
        refreshResult.catch((error) => {
          logger.error('WebSocket Delay refresh failed', getSafeErrorContext(error, {
            tripId,
            entityId: currentEntity?.entityId
          }));
        });
      }
    } else if (message.type === 'trip_plan_updated') {
      // Segment variant changed — refresh plan consumers
      logger.debug('WebSocket trip_plan_updated received', {
        tripId,
        entityId: currentEntity?.entityId,
        hasPlan: Boolean(message.plan),
        segmentCount: Array.isArray(message.plan?.segments) ? message.plan.segments.length : 0
      });
      onTripPlanUpdated?.(message.plan || null);
    }
  }, [acceptEtaUpdate, appendGpsTrailPoint, onEtaUpdate, onGpsTrailHistory, onLocationUpdate, onStatusUpdate, onWaypointStateChange, onArchivedTypeChange, onWaypointOrderChanged, onWaypointDeviationAlert, onTripBookingsChanged, onTripPlanUpdated, onGpsSourceConflict, currentEntity?.entityId, tripId, recordSelectedGpsFeedUpdate, status]);

  const sendLocationUpdate = useCallback(() => {
    // Location updates are server-authoritative: drivers post GPS through
    // Location V2, and the backend emits the trusted WebSocket update only
    // after authorization, persistence, cache update, and ETA queueing.
  }, []);

  const sendEtaUpdate = useCallback((etaData) => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) return;
    const clientKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
    if (shouldRejectEtaForNoneSource(effectiveEtaSource, etaData)) {
      WebSocketService.clearEtaData(clientKey);
      logger.debug('Skipped sending provider ETA because entity ETA source is none', {
        tripId,
        entityId: currentEntity.entityId,
        provider: etaData?.provider || null
      });
      return;
    }
    WebSocketService.sendEtaUpdate(clientKey, etaData);
  }, [currentEntity?.entityId, effectiveEtaSource, tripId, isEntityMismatch]);

  const sendStatusUpdate = useCallback((status) => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) return;
    const clientKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
    WebSocketService.sendStatusUpdate(clientKey, status);
  }, [currentEntity?.entityId, tripId, isEntityMismatch]);

  const getConnectionStatus = useCallback(() => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) return 'disconnected';
    const clientKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
    return WebSocketService.getConnectionStatus(clientKey);
  }, [currentEntity?.entityId, tripId, isEntityMismatch]);

  const fetchActualTripDuration = useCallback(async () => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) return;
    setEtaLoading(true);
    setEtaError(null);
    try {
      const response = await axios.get(`/api/trips/${currentEntity.entityId}/${tripId}/completed-trip-duration`);
      const actualData = response.data.data;
      setEtaData((prev) => ({
        ...prev,
        actual: actualData,
      }));
      logger.debug('Completed trip duration fetched', {
        tripId, 
        entityId: currentEntity?.entityId, 
        hasDuration: actualData?.actual_duration_minutes !== undefined && actualData?.actual_duration_minutes !== null
      });
      return actualData;
    } catch (error) {
      logger.error('Failed to fetch completed trip duration', getSafeErrorContext(error, {
        tripId, 
        entityId: currentEntity?.entityId 
      }));
      setEtaError(t('Failed to fetch completed trip duration'));
      toast.error(t('Failed to fetch completed trip duration'));
      throw error;
    } finally {
      setEtaLoading(false);
    }
  }, [currentEntity?.entityId, tripId, isEntityMismatch]);

  const fetchEstimatedRouteDuration = useCallback(async ({
    publishState = true,
    allowWhenEtaDisabled = false
  } = {}) => {
    if (!currentEntity?.entityId || !tripId || isEntityMismatch) return;
    if (effectiveEtaSource === 'none' && !allowWhenEtaDisabled) {
      logger.debug('Skipped reference route duration because entity ETA source is none', {
        tripId,
        entityId: currentEntity.entityId
      });
      return null;
    }
    // ETA-source none disables arrival-time authority, not the static route
    // segment distances needed by a scheduled trip's map, list, and rider card.
    // Callers opt in explicitly so live distance-only behavior remains unchanged.
    // Delay and waypoint-order reconciliation own stricter trip/session
    // generations than this shared hook. They can request data without letting
    // an old hook closure publish state or feedback before their owner check.
    const owner = publishState ? ++referenceRequestGeneration.current : null;
    const ownsPublication = () => !publishState || owner === referenceRequestGeneration.current;
    if (publishState) {
      setEtaLoading(true);
      setEtaError(null);
    }
    try {
      // Automatic reference reads resolve archive from the authorized trip row.
      // Entity preferences and retained client state cannot prove this choice.
      const response = await axios.get(`/api/trips/${currentEntity.entityId}/${tripId}/reference-route-duration`);
      if (!ownsPublication()) return null;
      const estimatedData = response.data.data;
      const operationalDelayDeclarations = Array.isArray(
        response.data.operational_delay_declarations
      )
        ? response.data.operational_delay_declarations
        : [];
      const operationalDelayProjectionWaypointOrderVersion =
        response.data.operational_delay_projection_waypoint_order_version;
      const suppressOperationalDelay = effectiveEtaSource === 'none';
      if (publishState) {
        setEtaData((prev) => {
          const next = {
            ...prev,
            estimated: estimatedData
          };
          if (suppressOperationalDelay) {
            delete next.estimatedOperationalDelayDeclarations;
            delete next.estimatedOperationalDelayProjectionWaypointOrderVersion;
          } else {
            next.estimatedOperationalDelayDeclarations = operationalDelayDeclarations;
            next.estimatedOperationalDelayProjectionWaypointOrderVersion =
              operationalDelayProjectionWaypointOrderVersion;
          }
          return next;
        });
        logger.debug('Reference route duration fetched', {
          tripId,
          entityId: currentEntity?.entityId,
          archivedType: response.data.archived_type,
          hasDuration: estimatedData?.estimated_duration_minutes !== undefined && estimatedData?.estimated_duration_minutes !== null
        });
      }
      // Keep the compatibility `data` waypoint array intact while returning
      // its sibling declaration projection to owner-scoped refresh callers.
      // The two values must settle together; otherwise an older label can be
      // paired with newly refreshed route rows.
      return suppressOperationalDelay
        ? { estimatedData }
        : {
            estimatedData,
            operationalDelayDeclarations,
            operationalDelayProjectionWaypointOrderVersion
          };
    } catch (error) {
      if (!ownsPublication()) return null;
      if (publishState) {
        logger.error('Failed to fetch reference route duration', getSafeErrorContext(error, {
          tripId,
          entityId: currentEntity?.entityId
        }));
        setEtaError(t('Failed to fetch reference route duration'));
        toast.error(t('Failed to fetch reference route duration'));
      }
      throw error;
    } finally {
      if (publishState && ownsPublication()) {
        setEtaLoading(false);
      }
    }
  }, [currentEntity?.entityId, effectiveEtaSource, tripId, isEntityMismatch]);

  const fetchMapAPIETA = useCallback(async () => {
    if (!currentEntity?.entityId || !tripId || !currentLocation || isEntityMismatch) return;
    const owner = ++referenceRequestGeneration.current;
    setEtaLoading(true);
    setEtaError(null);
    try {
      const mapEta = await etaService.updateETA(tripId, currentLocation, {
        ...currentEntity
      });
      if (owner !== referenceRequestGeneration.current) return null;
      if (!mapEta) {
        throw new Error('No ETA data returned from Map API');
      }
      setEtaData((prev) => ({
        ...prev,
        map: mapEta,
      }));
      sendEtaUpdate(mapEta);
      logger.debug('Map API ETA fetched and sent', {
        tripId, 
        entityId: currentEntity?.entityId, 
        ...getEtaLogSummary(mapEta)
      });
      return mapEta;
    } catch (error) {
      if (owner !== referenceRequestGeneration.current) return null;
      logger.error('Failed to fetch map API ETA', getSafeErrorContext(error, {
        tripId, 
        entityId: currentEntity?.entityId, 
        hasCurrentLocation: Boolean(currentLocation)
      }));
      setEtaError(t('Failed to fetch map API ETA'));
      toast.error(t('Failed to fetch map API ETA'));
      throw error;
    } finally {
      if (owner === referenceRequestGeneration.current) setEtaLoading(false);
    }
	  }, [tripId, currentLocation, currentEntity, sendEtaUpdate, isEntityMismatch]);

  useEffect(() => {
	    if (!currentEntity?.entityId || !tripId || !connectionTripData || isEntityMismatch) {
	      setConnectionStatus('disconnected');
	      return;
	    }

    // Prevent rapid reconnection
    const connectionKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
    if (connectionRef.current === connectionKey) {
        logger.debug('WebSocket already connected', {
          connectionKey,
          tripId,
          entityId: currentEntity?.entityId
        });
        return;
    }

    logger.debug('Setting up WebSocket connection', {
      connectionKey,
      tripId,
      entityId: currentEntity?.entityId,
      tripStatus: connectionTripData?.status
    });
    connectionRef.current = connectionKey;

    const cleanup = WebSocketService.connect(
      currentEntity.entityId,
      tripId,
      connectionTripData,
      handleWebSocketMessage
    );

    return () => {
      cleanup();
      setConnectionStatus('disconnected');
      connectionRef.current = null;
    };
	  }, [currentEntity?.entityId, tripId, connectionTripData, handleWebSocketMessage, isEntityMismatch]);

  useEffect(() => {
    if (!isDriver || !['scheduled', 'dispatched', 'in_progress'].includes(status) || isEntityMismatch) {
      setGpsActive(false);
      setCurrentLocation(null);
      setGpsError(null);
      return;
    }

    if (!navigator.geolocation) {
      setGpsError(t('GPS not available'));
      return;
    }

    let watchId = null;

    const handlePosition = async (position) => {
      if (!currentEntity?.entityId || !tripId || isEntityMismatch) {
        return;
      }
      const now = Date.now();
      const browserPositionTimestampMs = Number.isFinite(position.timestamp)
        ? Number(position.timestamp)
        : null;
      const location = normalizeLocationPayload({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        speed: position.coords.speed ?? null,
        heading: position.coords.heading || 0,
        timestamp: new Date().toISOString(),
      });

      // Solution 2: Update GPS callback timestamp for stall detection
      lastGpsCallbackTimeRef.current = now;
      setGpsStalled(false);
      toast.dismiss('gps-stall-warning');

      const postingPolicy = getLocationPostingPolicy({
        location,
        etaPayload: latestAcceptedEtaRef.current,
        tripData: latestTripDataRef.current,
        waypointStates: latestWaypointStatesRef.current,
      });
      if (postingPolicy.stalePositionMaxAgeMs !== null) {
        const positionAgeMs = browserPositionTimestampMs !== null
          ? now - browserPositionTimestampMs
          : null;

        if (positionAgeMs === null || positionAgeMs > postingPolicy.stalePositionMaxAgeMs) {
          logger.debug('Skipping stale transition-sensitive GPS sample', {
            tripId,
            entityId: currentEntity?.entityId,
            policyReason: postingPolicy.reason,
            targetWaypointId: postingPolicy.targetWaypointId,
            positionAgeMs,
            stalePositionMaxAgeMs: postingPolicy.stalePositionMaxAgeMs
          });
          return;
        }
      }

      const postDecision = shouldPostLocationUpdate({
        location,
        lastPostedLocation: lastPostedLocationRef.current || lastLocationRef.current,
        lastLogTime: lastLogTimeRef.current,
        now,
        postingPolicy,
        maxTimeWithoutLogMs: MAX_TIME_WITHOUT_LOG_MS,
        stationarySpeedThreshold: STATIONARY_SPEED_THRESHOLD,
      });

      // Log GPS if:
      // 1. Vehicle moved significantly using the current context-aware meter threshold, OR
      // 2. Max time elapsed AND vehicle is stationary (for stay duration tracking)
      const shouldLog = postDecision.shouldPost;

      if (!shouldLog) {
        return; // Skip logging
      }

      // Solution 1: Client-side throttle - respect minimum interval + backoff
      const timeSinceLastPost = now - lastPostTimeRef.current;
      const throttleInterval = MIN_POST_INTERVAL_MS + backoffMsRef.current;
      const isActiveMovementStatus = status === 'in_progress';
      if (timeSinceLastPost < throttleInterval) {
        // Still within throttle window, skip POST but update local state
        if (isActiveMovementStatus) {
          lastLocationRef.current = location;
          lastLogTimeRef.current = now;
          setCurrentLocation(location);
          appendGpsTrailPoint(location);
          setGpsActive(true);
        }
        return;
      }

      // Log the GPS point
      if (isActiveMovementStatus) {
        lastLocationRef.current = location;
        lastLogTimeRef.current = now;
        setCurrentLocation(location);
        appendGpsTrailPoint(location);
        setGpsActive(true);
      }
      setGpsError(null);

      try {
        const etaClientKey = `entity_${currentEntity.entityId}_trip_${tripId}`;
        const etaAdmission = WebSocketService.getEtaAdmission(etaClientKey);
        const response = await axios.post(`/api/v2/location/${currentEntity.entityId}/${tripId}/location`, {
          latitude: location.lat,
          longitude: location.lng,
          // Null means unavailable; an invalid report must not become derivable.
          speed: position.coords.speed == null ? null
            : Number.isFinite(position.coords.speed) ? position.coords.speed : 'invalid',
          heading: location.heading,
          timestamp: browserPositionTimestampMs == null ? null : new Date(browserPositionTimestampMs).toISOString(),
        });

        // Solution 1: Success - update post time and reset backoff
        lastPostTimeRef.current = now;
        lastPostedLocationRef.current = location;
        backoffMsRef.current = 0;

        if (isActiveMovementStatus) {
          onLocationUpdate?.(location);
        }

        const admittedEta = response.data?.eta && WebSocketService.storeLatestEta(
          etaClientKey, response.data.eta, Date.now(), { admission: etaAdmission, bootstrap: true }
        );
        if (admittedEta && acceptEtaUpdate(admittedEta, 'location_response')) {
          sendEtaUpdate(admittedEta);
        }
      } catch (error) {
        // Solution 1: Handle 429 rate limit responses
        if (error.response?.status === 429) {
          // Parse retryAfter from response body or header
          const retryAfter = error.response.data?.retryAfter || error.response.headers?.['retry-after'];
          backoffMsRef.current = (parseInt(retryAfter, 10) || 5) * 1000;
          lastPostTimeRef.current = now; // Start backoff window from the rate limit response
          logger.debug('Location rate limited, backing off', {
            tripId,
            retryAfter,
            backoffMs: backoffMsRef.current
          });
          // Don't show error to user for rate limiting - it's expected behavior
        } else if (!error.response || error.response.status >= 500) {
          setGpsError(t('Failed to update location'));
        }
      }
    };

    const handleError = (error) => {
      setGpsActive(false);
      setCurrentLocation(null);
      switch (error.code) {
        case error.PERMISSION_DENIED:
          setGpsError(t('GPS permission denied'));
          toast.error(t('GPS permission denied'));
          break;
        case error.POSITION_UNAVAILABLE:
          setGpsError(t('GPS unavailable'));
          toast.error(t('GPS unavailable'));
          break;
        case error.TIMEOUT:
          setGpsError(t('GPS request timed out'));
          toast.error(t('GPS request timed out'));
          break;
        default:
          setGpsError(t('GPS error occurred'));
          toast.error(t('GPS error occurred'));
      }
    };

    if (['scheduled', 'dispatched', 'in_progress'].includes(status)) {
      watchId = navigator.geolocation.watchPosition(
        handlePosition,
        handleError,
        {
          enableHighAccuracy: true,
          maximumAge: 10000,
          timeout: 30000,
        }
      );
    } else {
      setGpsActive(false);
      setCurrentLocation(null);
    }

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
      // Don't reset gpsActive here - it causes a flicker when transitioning
      // between valid statuses (e.g., dispatched -> in_progress).
      // State reset is handled at the start of the effect when conditions become invalid.
      // Only reset currentLocation to prevent stale data during watch restart.
      setCurrentLocation(null);
    };
  }, [tripId, status, isDriver, onLocationUpdate, sendEtaUpdate, isEntityMismatch, currentEntity?.entityId, acceptEtaUpdate, appendGpsTrailPoint, MIN_POST_INTERVAL_MS]);

  // Solution 2: GPS stall watchdog - warn driver if GPS updates stop
  useEffect(() => {
    if (!isDriver || !['dispatched', 'in_progress'].includes(status) || isEntityMismatch) {
      return; // Only monitor GPS stall for drivers with active trips
    }

    const checkGpsStall = () => {
      const stallDuration = Date.now() - lastGpsCallbackTimeRef.current;
      if (stallDuration > GPS_STALL_THRESHOLD_MS && !gpsStalled) {
        setGpsStalled(true);
        toast.warning(t('gpsUpdatesStalledWarning'), {
          toastId: 'gps-stall-warning', // Prevent duplicate toasts
          autoClose: false,
        });
        logger.warn('GPS stall detected', {
          tripId,
          stallDurationMs: stallDuration,
          status
        });
      }
    };

    // Initial check
    checkGpsStall();

    // Set up interval to check for stalls
    const watchdogInterval = setInterval(checkGpsStall, GPS_STALL_CHECK_INTERVAL_MS);

    return () => {
      clearInterval(watchdogInterval);
      // Dismiss stall warning toast on cleanup
      toast.dismiss('gps-stall-warning');
    };
  }, [isDriver, status, tripId, gpsStalled, isEntityMismatch]);

  useEffect(() => {
    let wakeLockObj = null;
    let isActive = true;

    const requestWakeLock = async () => {
      if (!isActive) return;
      if ('wakeLock' in navigator && ['scheduled', 'dispatched', 'in_progress'].includes(status)) {
        try {
          // Only request if we don't already have an active lock
          if (wakeLockObj && !wakeLockObj.released) return;

          wakeLockObj = await navigator.wakeLock.request('screen');
          setWakeLock(wakeLockObj);
          logger.debug('Wake lock acquired', { tripId, status });

          wakeLockObj.addEventListener('release', () => {
            logger.debug('Wake lock released', {
              tripId,
              status,
              reason: 'will re-acquire on visibility change'
            });
            setWakeLock(null);
          });
        } catch (err) {
          logger.debug('Wake lock not supported or failed', {
            tripId,
            status,
            ...(getSafeErrorDetails(err) || {})
          });
        }
      }
    };

    // Re-acquire wake lock when page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && ['scheduled', 'dispatched', 'in_progress'].includes(status)) {
        logger.debug('Page visible, re-acquiring wake lock', { tripId, status });
        requestWakeLock();
      }
    };

    if (['scheduled', 'dispatched', 'in_progress'].includes(status) && !isEntityMismatch) {
      requestWakeLock();
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      isActive = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockObj) {
        wakeLockObj.release().then(() => {
          setWakeLock(null);
        });
      }
    };
  }, [status, tripId, isEntityMismatch]);

  return {
    gpsActive,
    ...selectedGpsFeedReturnState,
    gpsError,
    gpsStalled,  // Solution 2: GPS stall state for UI indication
    currentLocation,
    setCurrentLocation,
    gpsTrail,
    connectionStatus,
    sendLocationUpdate,
    sendEtaUpdate,
    sendStatusUpdate,
    getConnectionStatus,
    getWebSocketEta,
    acceptEtaUpdate,
    etaData,
    etaLoading,
    etaError,
    fetchActualTripDuration,
    fetchEstimatedRouteDuration,
    fetchMapAPIETA,
  };
};
